import ReactDOM, { Root } from 'react-dom/client';
import { ThemeProvider, useTheme } from 'styled-components';

import App from './App';
import { Chat, ChatInner } from './components/Chat/Chat';
import { BetaBadge } from './components/ChatHeader/Header.styled';
import { ChatPanel } from './components/ChatPanel/ChatPanel';
import { LoadingEllipsis } from './components/Message/LoadingEllipsis';
import { OcChatWidget } from './components/OcWidget/OcChatWidget';
import { AppContextProvider } from './store/App/store';
import { GlobalStyleWrapper } from './styles/GlobalStyle';
import { AppConfig, AppEndpoints, EndpointConfigOptions } from './types/app';
import { OcChatConfig, OcWidgetOptions, RenderOcChat } from './types/oc';

export { ChatHeader } from './components/ChatHeader/ChatHeader';
export type {
  ActionStates,
  MessageReference,
  Messages,
  MessageStatus,
  MessageType,
} from './components/Message/types/messages';
export { MESSAGE_ROLE } from './components/Message/types/messages';
export { MessageInputBox } from './components/MessageInputBox/MessageInputBox';
export { useMessageInput } from './components/MessageInputBox/utils/useMessageInput';
export { Transcript } from './components/TranscriptBox/Transcript';
export type { TranscriptContextValue } from './store/transcript/store';
export { TranscriptContextProvider } from './store/transcript/store';
export type { TranscriptContextState } from './store/transcript/types';
export { useTranscriptContext } from './store/transcript/utils/useTranscriptContext';
export type { IAppTheme } from './styles/IAppTheme';
export type {
  AppConfig,
  AppEndpoints,
  EndpointConfigOptions,
} from './types/app';
export type {
  OcChatConfig,
  OcWidgetOptions,
  RenderOcChat,
} from './types/oc';

declare const __MOD_TYPE__: 'UMD' | 'ESM';

declare global {
  interface Window {
    hlAiAssistant: {
      renderInElement: AppConfig;
      unmountApp: (removeElement?: boolean) => void;
      renderOcChat: RenderOcChat;
      unmountOcChat: (removeElement?: boolean) => void;
    };
  }
}

export {
  AppContextProvider,
  BetaBadge,
  Chat,
  ChatInner,
  ChatPanel,
  LoadingEllipsis,
  ThemeProvider,
  useTheme,
};

let rootElement: HTMLElement | null;
let rootApp: Root | null;

// OC Widget root tracking
let ocRootElement: HTMLElement | null;
let ocRootApp: Root | null;

export const AiAssistant = ({
  endpoints,
  config,
  startOpen = false,
}: {
  endpoints: AppEndpoints;
  config: EndpointConfigOptions;
  startOpen: boolean;
}) => {
  return (
    <AppContextProvider endpoints={endpoints} config={config}>
      <App startOpen={startOpen} />
    </AppContextProvider>
  );
};

/**
 * Render the React App in a specific element
 * @param {HTMLElement} element - Element in which to render the app
 * @param {EndpointConfigOptions} config - Endpoint config for the app
 * @param {AppEndpoints} endpoints - Endpoints for the app
 * @param {boolean} startOpen - Optionally start the app open
 */
export const renderInElement: AppConfig = (
  element: HTMLElement,
  config: EndpointConfigOptions,
  endpoints: AppEndpoints,
  startOpen: boolean = false,
) => {
  const root = ReactDOM.createRoot(element);

  rootElement = element;
  rootApp = root;

  root.render(
    <>
      <GlobalStyleWrapper />
      <AiAssistant
        endpoints={endpoints}
        config={config}
        startOpen={startOpen}
      />
    </>,
  );
};

/**
 * Unmounts the React App
 *
 * Empties or Removes the Root Element of App
 * @param {boolean} [removeElement] - Optionally remove the element from DOM
 */
export const unmountApp = (removeElement?: boolean) => {
  if (rootApp !== null && rootElement !== null) {
    rootApp.unmount();

    if (removeElement) {
      rootElement.remove();
    } else {
      rootElement.innerHTML = '';
    }
  }
};

/**
 * Render OC Chat Widget
 * @param {HTMLElement | string} elementOrId - Element or element ID to render in
 * @param {OcChatConfig} config - Required OC configuration
 * @param {OcWidgetOptions} options - Optional customization options (includes UI config)
 */
export const renderOcChat: RenderOcChat = async (
  elementOrId: HTMLElement | string,
  config: OcChatConfig,
  options?: OcWidgetOptions,
) => {
  // Get the element from ID if string provided
  const element =
    typeof elementOrId === 'string'
      ? document.getElementById(elementOrId)
      : elementOrId;

  if (!element) {
    const error = new Error(
      typeof elementOrId === 'string'
        ? `Element with id "${elementOrId}" not found`
        : 'Invalid element provided',
    );
    if (options?.onError) {
      options.onError(error);
    }
    throw error;
  }

  // Validate required config
  if (!config.aiAssistantKey || !config.iamKey || !config.tenantId || !config.loginUrl) {
    const error = new Error(
      'Missing required configuration: aiAssistantKey, iamKey, tenantId, and loginUrl are required',
    );
    if (options?.onError) {
      options.onError(error);
    }
    throw error;
  }

  const root = ReactDOM.createRoot(element);

  ocRootElement = element;
  ocRootApp = root;

  // Render the widget component
  // It will handle auth checking, login UI, error UI, and chat rendering
  ocRootApp.render(<OcChatWidget config={config} options={options} />);
};

/**
 * Unmount OC Chat Widget
 * @param {boolean} removeElement - Optionally remove the element from DOM
 */
export const unmountOcChat = (removeElement: boolean = false) => {
  if (ocRootApp !== null && ocRootElement !== null) {
    ocRootApp.unmount();

    if (removeElement) {
      ocRootElement.remove();
    } else {
      ocRootElement.innerHTML = '';
    }
  }
};

if (__MOD_TYPE__ === 'UMD') {
  window.hlAiAssistant = {
    renderInElement,
    unmountApp,
    renderOcChat,
    unmountOcChat,
  };
}
